import React, { useEffect, useRef, useState } from "react";
import jsonExport from "jsonexport/dist";

import { API, graphqlOperation } from "aws-amplify";
import {
  searchInboundOpen,
  searchOutboundOpen,
} from "../../../graphql/report-queries";

import {
  Box,
  Card,
  Grid,
  makeStyles,
  Typography,
  useMediaQuery,
} from "@material-ui/core";

import {
  Button,
  Datagrid,
  downloadCSV,
  ListContextProvider,
  SimpleForm,
  SimpleList,
  TextField,
  Title,
  TopToolbar,
  useList,
  useNotify,
} from "react-admin";

import { InboundStatus } from "../../../utils/constants";
import { logError } from "../../../utils/appUtils";
import { formatInYardTimeZone } from "../../../utils/dateUtils";

import {
  ChevronRightIcon,
  ExportIcon,
} from "../../../components/common/iconography";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { YardDateField } from "../../../components/common/YardDateField";
import { Pagination } from "../../../components/Pagination";
import { BackButton } from "../../../components/common/BackButton";
import SwitchOnOff from "../../../components/common/AntSwitch";
import { AccountDropDown } from "../../../components/common/AccountDropDown";

const exporter = (shipments, isOutbound) => {
  const shipmentsForExport = [];
  const fieldSet = new Set();
  const fileName = isOutbound
    ? `outboundOpenOrders_${new Date().toLocaleDateString()}`
    : `inboundOpenOrders_${new Date().toLocaleDateString()}`;

  // Order of columns you care about
  const columnOrder = [
    { key: "orderNumber", label: "Order Number" },
    { key: "pickupNumber", label: "Pickup Number" },
    { key: "railCarNumber", label: "Car/Trl Number" },
    { key: "carrier", label: "Carrier" },
    { key: "placedDateTime", label: "Arrival Date" },
  ];

  for (const shipment of shipments) {
    const row = {};

    row.orderNumber = shipment.poNumber;
    fieldSet.add("orderNumber");

    if (shipment.pickupNumber) {
      row.pickupNumber = shipment.pickupNumber;
      fieldSet.add("pickupNumber");
    }

    row.railCarNumber = shipment.railCarNumber;
    fieldSet.add("railCarNumber");

    row.carrier = shipment.carrierName;
    fieldSet.add("carrier");

    // Yard time zone, matching the on-screen list — a browser-local date
    // shifts the day for anyone exporting from outside the yard's zone.
    row.placedDateTime = formatInYardTimeZone(
      new Date(
        shipment.placedDateTime ??
          shipment.placementDateTime ??
          shipment.arrivalDateTime,
      ),
      "MM/dd/yyyy",
    );
    fieldSet.add("placedDateTime");

    shipmentsForExport.push(row);
  }

  // Filter columnOrder based on actual fields used
  const filteredColumns = columnOrder.filter((col) => fieldSet.has(col.key));

  // Build field and rename arrays in order
  const fields = filteredColumns.map((col) => col.key);
  const rename = filteredColumns.map((col) => col.label);

  jsonExport(
    shipmentsForExport,
    {
      fields,
      rename,
    },
    (err, csv) => {
      downloadCSV(csv, fileName);
    },
  );
};

const useStyles = makeStyles((theme) => ({
  toolbarRoot: {
    padding: theme.spacing(0),
  },
  buttonsRight: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    // border: "solid 1px white",
  },
  controlsLeft: {
    display: "flex",
    gap: theme.spacing(2),
    // border: "solid 1px blue",
  },
  // Responsive order switching
  gridItemButtons: {
    order: 1,
    [theme.breakpoints.up("md")]: {
      order: 2,
    },
  },
  gridItemFilters: {
    order: 2,
    [theme.breakpoints.up("md")]: {
      order: 1,
    },
  },
}));

const ListActions = ({
  finalData,
  accountId,
  setAccountId,
  isOutbound,
  setIsOutbound,
  getOpenOrders,
}) => {
  const classes = useStyles();
  const smDown = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  return (
    <TopToolbar className={classes.toolbarRoot}>
      <Grid container spacing={1}>
        {/* Buttons: Export, Refresh, Back */}
        <Grid item xs={12} md={5} className={classes.gridItemButtons}>
          <Box className={classes.buttonsRight}>
            <Button
              label="Export"
              disabled={finalData?.length === 0}
              onClick={() => exporter(finalData, isOutbound)}
            >
              <ExportIcon />
            </Button>
            <BackButton basePath="/reports" />
          </Box>
        </Grid>

        {/* Filters: Account, Toggle, Run */}
        <Grid item xs={12} md={7} className={classes.gridItemFilters}>
          <Box className={classes.controlsLeft}>
            <AccountDropDown
              source="accountId"
              label="Account"
              allowEmpty={false}
              onAccountChange={(value) => setAccountId(value?.id)}
              helperText={false}
            />
            <SwitchOnOff
              source="inout"
              leftLabel={smDown ? "In" : "Inbound"}
              rightLabel={smDown ? "Out" : "Outbound"}
              checked={isOutbound}
              setChecked={setIsOutbound}
            />
            <Button
              label="Run"
              variant="contained"
              color="default"
              disabled={!accountId}
              onClick={getOpenOrders}
              style={{ alignSelf: "center" }}
            />
          </Box>
        </Grid>
      </Grid>
    </TopToolbar>
  );
};

export const OpenOrdersReport = (props) => {
  const notify = useNotify();
  const initFilterValues = {
    reloadYardId: { eq: window.appUser?.reloadYardId },
    and: [
      // Status list apples to both inbound and outbound
      { status: { ne: InboundStatus.RELEASED } },
      { status: { ne: InboundStatus.AGGREGATED } },
      { status: { ne: InboundStatus.ARCHIVED } },
    ],
  };

  const [accountId, setAccountId] = useState("");
  const [isOutbound, setIsOutbound] = useState(false);

  const [finalData, setFinalData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Bumped on every run and every criteria change. A run that finishes after
  // either drops its rows, so the grid never shows inbound orders under the
  // Outbound switch, or another account's orders.
  const requestRef = useRef(0);

  const getOpenOrders = async () => {
    const requestId = ++requestRef.current;
    const isCurrent = () => requestId === requestRef.current;
    try {
      setLoading(true);
      const limit = 1000;

      // Paged on id, not poNumber: each page starts after the last sort value,
      // so a page boundary inside one PO would skip that PO's other cars.
      const searchInput = {
        sort: { field: "id", direction: "asc" },
        filter: {
          ...initFilterValues,
        },
        limit,
      };
      if (accountId) {
        searchInput.filter.accountId = { eq: `${accountId}` };
      }

      let shipments = [];
      let nextToken = true;
      while (nextToken) {
        const input =
          nextToken === true ? searchInput : { ...searchInput, nextToken };
        if (isOutbound) {
          const result = await API.graphql(
            graphqlOperation(searchOutboundOpen, input),
          );
          shipments.push(...result.data.searchOutboundShipments.items);
          if (result.data.searchOutboundShipments.items.length < limit) break;
          nextToken = result.data.searchOutboundShipments.nextToken;
        } else {
          const result = await API.graphql(
            graphqlOperation(searchInboundOpen, input),
          );
          shipments.push(...result.data.searchInboundShipments.items);
          if (result.data.searchInboundShipments.items.length < limit) break;
          nextToken = result.data.searchInboundShipments.nextToken;
        }
      }

      if (!isCurrent()) return;
      // PO order for the export, which takes rows as they are, not as the
      // grid sorts them.
      shipments.sort((a, b) =>
        (a.poNumber ?? "").localeCompare(b.poNumber ?? ""),
      );
      setFinalData(shipments);
    } catch (err) {
      if (!isCurrent()) return;
      logError("OpenOrdersReport:getOpenOrders", err, notify);
      setFinalData([]);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  useEffect(() => {
    requestRef.current += 1;
    setFinalData([]);
    setLoading(false);
  }, [accountId, isOutbound]);

  const fixedFilter = ""; // {"lowInventoryAlert": true};
  const defaultSort = { field: "poNumber", order: "ASC" };
  const defaultPerPage = 25;

  // console.log("finalData", finalData);

  const listContext = useList({
    ...props,
    data: finalData,
    ids: finalData.map(({ id }) => id),
    loading,
    loaded: !loading,
    filter: fixedFilter,
    sort: defaultSort,
    perPage: defaultPerPage,
  });

  return (
    <SimpleForm toolbar={false}>
      <ListContextProvider value={listContext}>
        <Title title={<ForceTitle title="Open Orders" />} />
        <ListActions
          {...props}
          finalData={finalData}
          accountId={accountId}
          setAccountId={setAccountId}
          isOutbound={isOutbound}
          setIsOutbound={setIsOutbound}
          getOpenOrders={getOpenOrders}
        />
        <Card>
          <ShipmentGrid isOutbound={isOutbound} />
          <Pagination {...listContext} />
        </Card>
      </ListContextProvider>
    </SimpleForm>
  );
};

const ShipmentGrid = ({ isOutbound, ...props }) => {
  const xsDown = useMediaQuery((theme) => theme.breakpoints.down("xs"));

  return xsDown ? (
    <SimpleList
      {...props}
      primaryText={(record) => record.railCarNumber}
      secondaryText={(record) => (
        <Typography>{`Carrier: ${record.carrierName ?? ""}`}</Typography>
      )}
      tertiaryText={(record) => record.poNumber}
      rightIcon={() => (
        <ChevronRightIcon
          fontSize="large"
          color="secondary"
          style={{ marginLeft: 40, marginTop: 10 }}
        />
      )}
      linkType="edit"
    />
  ) : (
    <Datagrid rowClick="edit" {...props}>
      <TextField source="poNumber" label="Order No" sortable={true} />
      {isOutbound && (
        <TextField source="pickupNumber" label="Pickup No" sortable={true} />
      )}
      <TextField source="railCarNumber" label="Car/Trl No" sortable={true} />
      <TextField source="carrierName" label="Carrier" sortable={true} />
      {isOutbound ? (
        // Placed lives in placementDateTime; arrivalDateTime holds it for
        // records placed before the placement backfill ran. Rendered in the
        // yard's time zone, not the viewer's browser zone.
        <YardDateField
          source="placementDateTime"
          fallbackSource="arrivalDateTime"
          label="Time Placed"
          showTime={true}
          sortable={true}
        />
      ) : (
        <YardDateField
          source="placedDateTime"
          label="Time Placed"
          showTime={true}
          sortable={true}
        />
      )}
    </Datagrid>
  );
};
